import { useState } from "react";
import { motion } from "framer-motion";
import { Award, X, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CERTIFICATES } from "@/data/awards";
import { resolveAssetPath } from "@/lib/assets";

const withYear = (c) => (c.year && !c.issuer.includes(c.year) ? `${c.issuer} · ${c.year}` : c.issuer);
const GOLD = "bg-[linear-gradient(135deg,#8a6a1f_0%,#f3dc8b_25%,#b8892e_50%,#f7e7a6_75%,#8a6a1f_100%)]";

const Frame = ({ cert, big, onOpen, index }) => (
  <motion.button
    type="button"
    onClick={() => onOpen(cert)}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 5) * 0.06 }}
    className="group flex flex-col h-full w-full text-left"
    data-testid={`certificate-${cert.id}`}
  >
    <div className={`${GOLD} p-[5px] rounded-[6px] shadow-[0_18px_36px_-16px_rgba(11,37,69,0.45)] transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_28px_50px_-18px_rgba(11,37,69,0.55)]`}>
      <div className="bg-[#1d140b] p-[3px] rounded-[3px]">
        <div className={`relative bg-[#fbf8f1] ${big ? "aspect-[4/3] p-6 md:p-8" : "aspect-square p-4"} flex items-center justify-center shadow-[inset_0_0_0_1px_#e8dcc2,inset_0_4px_14px_rgba(0,0,0,0.12)]`}>
          <img src={resolveAssetPath(cert.image)} alt={`${cert.title} - ${cert.issuer}`} loading="lazy" className="max-w-full max-h-full object-contain shadow-[0_6px_16px_rgba(0,0,0,0.25)]" />
          <span className="absolute inset-0 bg-[#0b2545]/0 group-hover:bg-[#0b2545]/10 transition-colors flex items-center justify-center">
            <ZoomIn className="w-8 h-8 text-white drop-shadow opacity-0 group-hover:opacity-100 transition-opacity" />
          </span>
        </div>
      </div>
    </div>
    <div className="pt-4 text-center flex-1">
      <div className={`font-semibold text-[var(--navy)] leading-snug ${big ? "text-[16px] md:text-[18px]" : "text-[13.5px]"} line-clamp-2`}>{cert.title}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[#b8892e] font-semibold line-clamp-2">{withYear(cert)}</div>
    </div>
  </motion.button>
);

export const AwardsWall = ({ limit, heading = true }) => {
  const [open, setOpen] = useState(null);
  const list = limit ? CERTIFICATES.slice(0, limit) : CERTIFICATES;
  const [featured, rest] = [list.slice(0, 2), list.slice(2)];
  return (
    <section className="relative bg-[#f7f3ea] py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="awards-wall" id="awards">
      <div className="absolute inset-x-0 top-0 h-64 bg-[radial-gradient(ellipse_at_top,rgba(245,158,11,0.18),transparent_70%)] pointer-events-none" />
      {heading && (
        <div className="relative max-w-3xl mx-auto text-center mb-14">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold text-[#b8892e]"><Award className="w-4 h-4" /> Awards & Recognition</div>
          <h2 className="font-serif-display text-3xl md:text-[44px] text-[var(--navy)] mt-3 leading-tight">Honoured for great holidays</h2>
          <div className="mx-auto mt-4 h-px w-24 bg-gradient-to-r from-transparent via-[#b8892e] to-transparent" />
          <p className="text-[14px] text-gray-600 mt-4">Recognitions from the travel industry, government bodies and media partners. Tap any certificate to view it in full.</p>
        </div>
      )}
      <div className="relative max-w-[1180px] mx-auto">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto mb-14" data-testid="awards-featured">
          {featured.map((c, i) => <Frame key={c.id} cert={c} big index={i} onOpen={setOpen} />)}
        </div>
        {rest.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-10 md:gap-x-7" data-testid="awards-grid">
            {rest.map((c, i) => <Frame key={c.id} cert={c} index={i} onOpen={setOpen} />)}
          </div>
        )}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-5xl bg-[#0f0b07] border-[#3a2818] p-3 sm:p-5 [&>button]:hidden" data-testid="certificate-lightbox">
          {open && (
            <div>
              <div className="flex items-start justify-between gap-4 mb-3">
                <DialogTitle className="text-white text-[15px] font-semibold">{open.title}<span className="block text-[12px] text-[#f3dc8b] font-normal mt-0.5">{withYear(open)}</span></DialogTitle>
                <button type="button" onClick={() => setOpen(null)} className="text-white/80 hover:text-white" data-testid="certificate-lightbox-close" aria-label="close"><X className="w-5 h-5" /></button>
              </div>
              <img src={resolveAssetPath(open.image)} alt={open.title} className="w-full max-h-[78vh] object-contain bg-[#fbf8f1] p-2" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
